import { ApiError } from '../../../core/errors'
import { HttpRequest } from '../../../core/interfaces/http'
import { RpcResponse, RpcRequest } from '../interfaces'
import { RpcErrorCode } from './RpcErrorCode.enum'
import { ErrorCategory } from '../../../core/interfaces/error/ErrorCategory.Enum'

const getMessageFromErrorCode = (c: RpcErrorCode | number): string => {
  if (c === RpcErrorCode.INTERNAL_ERROR) return 'INTERNAL ERROR'
  if (c === RpcErrorCode.INVALID_PARAMS) return 'INVALID PARAMS'
  if (c === RpcErrorCode.INVALID_REQUEST) return 'INVALID REQUEST'
  if (c === RpcErrorCode.METHOD_NOT_FOUND) return 'METHOD NOT FOUND'
  if (c === RpcErrorCode.PARSE_ERROR) return 'PARSE ERROR'
  if (c === RpcErrorCode.SERVER_ERROR
    || (c <= -32000 && c >= -32099)
  ) return 'SERVER ERROR'
  if (typeof c === 'number' && (c <= -32768 || c >= -32000)) {
    return 'APPLICATION DEFINED ERROR'
  }
  // fallback:
  return 'UNKNOWN / ILLEGAL ERROR CODE'
}

// Map error code to Category
const getCat = (code: RpcErrorCode | number): ErrorCategory => {
  if (code === RpcErrorCode.INTERNAL_ERROR) return ErrorCategory.SERVER_PROBLEM_FATAL
  if (code === RpcErrorCode.INVALID_REQUEST) return ErrorCategory.REQUEST_PROBLEM
  if (code === RpcErrorCode.METHOD_NOT_FOUND) return ErrorCategory.REQUEST_PROBLEM
  if (code === RpcErrorCode.PARSE_ERROR) return ErrorCategory.REQUEST_PROBLEM
  // jsonrpc Invalid params covers both missing params and invalid param values
  // so we can't just return ErrorCategory.REQUEST_INVALID_PARAM_VALUES
  if (code === RpcErrorCode.INVALID_PARAMS) return ErrorCategory.REQUEST_PROBLEM
  if (code === RpcErrorCode.SERVER_ERROR
    || (code <= -32000 && code >= -32099)
  ) return ErrorCategory.SERVER_PROBLEM_FATAL
  // outside reserved jsonrpc range: application defined error codes
  if (typeof code === 'number' && (code <= -32768 || code >= -32000)) {
    return ErrorCategory.REQUEST_PROBLEM
  }
  // fallback:
  return ErrorCategory.UNKNOWN
}

/**
 * A JsonRpcError occurs if a jsonrpc API responds with a json rpc error
 * response as defined by the JsonRPC spec.
 *
 * If we get this error it means that there is no error in the network or
 * http layer (otherwise we would not receive a jsonrpc response at all).
 *
 * Inspect the code/message/data for details.
 *
 * Note: this Error class is NOT a JsonRpcError as defined by
 * the jsonRpc spec, but a decorated version based on ApiError.
 */
export class JsonRpcError extends ApiError {
  public readonly rpcResponse: RpcResponse<unknown>

  public readonly rpcRequest: RpcRequest<unknown>

  public readonly rpcErrorData: unknown

  /**
   *
   * @param args
   */
  constructor(args: {
    code: RpcErrorCode | number; // one of the jsonrpc error codes
    message?: string;
    httpRequest: HttpRequest;
    rpcResponse: RpcResponse<unknown>;
  }) {
    const myMessage = args.message ? args.message : getMessageFromErrorCode(args.code)
    const cat = getCat(args.code)
    const rpcRequest = args.httpRequest.data as RpcRequest<unknown>
    const rpcErrorData = args.rpcResponse.error?.data
    let extraExcerpt: { [key: string]: unknown } = {}
    if (cat === ErrorCategory.REQUEST_PROBLEM) {
      extraExcerpt = { 'rpc-request': rpcRequest }
    }
    if (rpcErrorData) {
      extraExcerpt = { ...extraExcerpt, 'rpc-error-data': rpcErrorData }
    }
    super({
      ...args,
      message: `JsonRpc error: '${myMessage}' (${args.code})`,
      category: cat,
      excerptExtra: extraExcerpt,
    })
    this.rpcResponse = args.rpcResponse
    this.rpcRequest = rpcRequest
    this.rpcErrorData = rpcErrorData
    Object.setPrototypeOf(this, JsonRpcError.prototype)
  }

  public toJSON() {
    return JSON.stringify({
      ...this.getBaseJSON(),
      jsonRpc: {
        request: this.rpcRequest,
        response: this.rpcResponse,
        errorData: this.rpcErrorData,
      },
    })
  }
}
